import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Map, MapRef } from "react-map-gl/dist/maplibre"
import * as Icons from "@nova/component-icons"
import { StyleSpecification } from "maplibre-gl"
import { useAppSelector } from "integrations/redux/hooks"
import { useMapStylesJSON } from "features/map/control/selectors"
import { selectActiveWorkspace } from "features/data-management/workspace/WorkspaceManagementSlice"
import { DeckGLOverlay, fitMapToBounds } from "features/map/MapPreview"
import VideoLayer from "features/map/layers/video/Video.class"
import { droneIconColors } from "features/map/layers/video/VideoDroneLayer"
import { VideoMetadata } from "@nova/slice-video"
import { parseFlightPathFile } from "./parseFlightPath"

export interface LatLng {
    lat: number
    lng: number
}

interface FlightPathMapPreviewProps {
    flightPathFile?: File | null
    takeoffCoords?: LatLng | null
    onTakeoffChange?: (coords: LatLng) => void
    className?: string
}

export const FlightPathMapPreview: FunctionComponent<FlightPathMapPreviewProps> = ({
    flightPathFile,
    takeoffCoords,
    onTakeoffChange,
    className = "h-[200px]",
}) => {
    const mapStylesJSON = useMapStylesJSON()
    const workspace = useAppSelector(selectActiveWorkspace())
    const workspaceLocation = workspace?.location
    const flatMapStyle = useMemo(() => {
        const { terrain: _terrain, ...rest } = mapStylesJSON as StyleSpecification & {
            terrain?: unknown
        }
        return rest as StyleSpecification
    }, [mapStylesJSON])

    const mapRef = useRef<MapRef | null>(null)
    const [coords, setCoords] = useState<[number, number][] | null>(null)

    useEffect(() => {
        let cancelled = false
        if (!flightPathFile) {
            setCoords(null)
            return
        }
        parseFlightPathFile(flightPathFile)
            .then((parsed) => {
                if (!cancelled) setCoords(parsed.length > 0 ? parsed : null)
            })
            .catch(() => {
                if (!cancelled) setCoords(null)
            })
        return () => {
            cancelled = true
        }
    }, [flightPathFile])

    const layers = useMemo(() => {
        if (!coords) return []
        const data = coords.map(([lon, lat]): VideoMetadata[number] => [lon, lat, 0, 0, 0, 0, 0, 0])
        return [
            new VideoLayer({
                id: "manual-flight-path",
                flightId: "manual-flight-path",
                frameNumber: 1,
                mediaType: "image",
                frameEnabled: false,
                crosshairEnabled: false,
                live: true,
                color: droneIconColors[0],
                getVideoElement: () => null,
                data: data as VideoMetadata,
                flightPathEnabled: true,
                droneEnabled: false,
            }),
        ]
    }, [coords])

    const fit = useCallback(() => {
        if (!mapRef.current || !coords?.length) return
        fitMapToBounds(mapRef.current, coords)
    }, [coords])

    useEffect(() => {
        fit()
    }, [coords, fit])

    const showPin = !flightPathFile

    useEffect(() => {
        if (!showPin || !mapRef.current || !takeoffCoords) return
        const { lat, lng } = takeoffCoords
        if (!isFinite(lat) || !isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180)
            return
        const center = mapRef.current.getCenter()
        if (Math.abs(center.lat - lat) < 1e-4 && Math.abs(center.lng - lng) < 1e-4) return
        mapRef.current.setCenter([lng, lat])
    }, [takeoffCoords, showPin])

    const handleMove = () => {
        if (flightPathFile || !onTakeoffChange || !mapRef.current) return
        const c = mapRef.current.getCenter()
        onTakeoffChange({ lat: c.lat, lng: c.lng })
    }

    return (
        <div className={`relative w-full overflow-hidden rounded-md ${className}`}>
            <Map
                ref={mapRef}
                mapStyle={flatMapStyle}
                initialViewState={{
                    latitude:
                        takeoffCoords && isFinite(takeoffCoords.lat)
                            ? takeoffCoords.lat
                            : (workspaceLocation?.latitude ?? 39.5),
                    longitude:
                        takeoffCoords && isFinite(takeoffCoords.lng)
                            ? takeoffCoords.lng
                            : (workspaceLocation?.longitude ?? -98.35),
                    zoom: takeoffCoords ? 12 : (workspaceLocation?.zoom ?? 3),
                }}
                attributionControl={false}
                onLoad={fit}
                onMove={handleMove}
                style={{ width: "100%", height: "100%" }}
            >
                <DeckGLOverlay layers={layers} />
            </Map>
            {showPin && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <Icons.Icon
                        Icon={Icons.PinDrop}
                        className="text-red-500 -translate-y-1/2 drop-shadow-md"
                    />
                </div>
            )}
        </div>
    )
}
